import { ProductMatrixSectionGeneral } from '@app/components/products/ProductMatrix/ProductMatrixSectionGeneral';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getActiveDeliveryWindowsIdsForOrderBuyer } from '@app/ducks';
import { getOrderDetails, getOrderStatus } from '@app/ducks/order';
import { getDeliveryWindowIdsFromVariants, getIsExpired } from '@app/logic/deliveryWindows';
import {
  getDeliveryWindowsFromProducts,
  getIsOrderEditable,
  getProductByProductIdVariantIdFromProducts,
  getTotalProductPriceInProductVariants,
  getTotalUnitsInProductVariants,
  getTotalVariantQuantityInProductVariants,
  getUnitsCountInDelwin,
} from '@app/logic/Orders';
import ProductsLogic, { getUrlForProductAndVariant } from '@app/logic/products';
import { cn } from '@app/utils/cn';
import { useIsScreenSize } from '@app/utils/hooks';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import { prop, uniqBy } from 'ramda';
import React from 'react';
import { useSelector } from 'react-redux';

import { CheckoutProductAlerts } from './CheckoutProductAlerts';
import { CheckoutProductInfo } from './CheckoutProductInfo';
import { CheckoutProductInfoMobile } from './CheckoutProductInfoMobile';

interface Props {
  productInfo: Product.Standard;
  deliveryWindowIds: string[];
}
export interface SummaryValues {
  variantsCount: number;
  unitsCount: number;
  price: number;
}

export const CheckoutProduct = (props: Props) => {
  const { deliveryWindowIds, productInfo } = props;
  const productId = productInfo.product;
  const variantId = productInfo.variant;
  const orderDetails = useSelector(getOrderDetails);
  const activeDeliveryWindowsIds = useSelector(getActiveDeliveryWindowsIdsForOrderBuyer);
  const showMobileVersion = useIsScreenSize(BREAKPOINTS.MD);
  const orderStatus = useSelector(getOrderStatus);
  const orderProductInfo = React.useMemo(
    () => getProductByProductIdVariantIdFromProducts(productId, variantId, orderDetails.order.products),
    [productId, variantId, orderDetails.order.products],
  );

  const orderProductVariantsInDeliveryWindows = React.useMemo(
    () =>
      orderDetails.order.products.filter(product => deliveryWindowIds.includes(product.deliveryWindow) && product.product === productId),
    [deliveryWindowIds, orderDetails.order.products, productId],
  );

  const orderProductDeliveryWindows = getDeliveryWindowsFromProducts(orderProductVariantsInDeliveryWindows, orderDetails.deliveryWindows);
  const expiredOrderProductDeliveryWindows = React.useMemo(
    () => orderProductDeliveryWindows.filter(deliveryWindow => getIsExpired(deliveryWindow, activeDeliveryWindowsIds)),
    [activeDeliveryWindowsIds, orderProductDeliveryWindows],
  );

  const expiredOrderVariants = React.useMemo(() => {
    const expiredOrderDelwinIds = expiredOrderProductDeliveryWindows.map(deliveryWindow => deliveryWindow.deliveryWindow);

    return orderProductVariantsInDeliveryWindows.filter(({ deliveryWindow }) => expiredOrderDelwinIds.includes(deliveryWindow));
  }, [expiredOrderProductDeliveryWindows, orderProductVariantsInDeliveryWindows]);

  const cancelledOrderVariants = React.useMemo(
    () => orderProductVariantsInDeliveryWindows.filter(product => product.isCancelled),
    [orderProductVariantsInDeliveryWindows],
  );
  const uniqueCancelledOrderVariants = React.useMemo(() => uniqBy(prop('variant'), cancelledOrderVariants), [cancelledOrderVariants]);

  const summaryValues = React.useMemo(
    () => ({
      price: getTotalProductPriceInProductVariants(orderProductVariantsInDeliveryWindows),
      unitsCount: getTotalUnitsInProductVariants(orderProductVariantsInDeliveryWindows),
      variantsCount: getTotalVariantQuantityInProductVariants(orderProductVariantsInDeliveryWindows),
    }),
    [orderProductVariantsInDeliveryWindows],
  );

  /**
   * This is probably deleted product - should be present in `order.deletedProducts`.
   */
  if (!orderProductInfo) {
    return null;
  }

  const isOrderEditable = getIsOrderEditable(orderStatus);

  const isOrderProductCancelled = cancelledOrderVariants.length === orderProductVariantsInDeliveryWindows.length;
  const isOrderProductExpired = expiredOrderVariants.length === orderProductVariantsInDeliveryWindows.length;
  const deliveryWindowIdsWithUnits = deliveryWindowIds.filter(
    deliveryWindowId => getUnitsCountInDelwin(deliveryWindowId, orderProductVariantsInDeliveryWindows) > 0,
  );
  const variants = ProductsLogic.getAllOrderedVariants(productInfo);
  const delwinsFromVariants = getDeliveryWindowIdsFromVariants(variants);
  const hasNoActiveDeliveryWindows = isEmpty(delwinsFromVariants);

  const isProductNotAvailable =
    !orderProductInfo.isEditable || hasNoActiveDeliveryWindows || isOrderProductCancelled || isOrderProductExpired;

  const link = !isOrderProductExpired && !isOrderProductCancelled ? getUrlForProductAndVariant({ productId, variantId }) : undefined;

  return (
    <div>
      {isOrderEditable && (
        <div className="px-4 lg:px-8">
          <CheckoutProductAlerts cancelledVariants={uniqueCancelledOrderVariants} />
        </div>
      )}
      <div className="mx-auto rounded-regular bg-white py-3 md:p-4">
        {showMobileVersion ?
          <CheckoutProductInfoMobile
            productInfo={productInfo}
            summaryValues={summaryValues}
            isDisabled={isProductNotAvailable}
            link={link}
          />
        : <CheckoutProductInfo productInfo={productInfo} summaryValues={summaryValues} isDisabled={isProductNotAvailable} link={link} />}
        <div className={cn('overflow-hidden', { 'pointer-events-none opacity-50': isProductNotAvailable && isOrderEditable })}>
          <ProductMatrixSectionGeneral
            showOnlyDeliveryWindows={deliveryWindowIdsWithUnits}
            variants={variants}
            attributesSource="listing"
            isReadOnly
          />
        </div>
      </div>
    </div>
  );
};
